import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

type TrackingStatus = 'confirmed' | 'processing' | 'dispatched' | 'in_transit' | 'delivered';

interface TrackingStep {
  id: TrackingStatus;
  label: string;
  description: string;
  icon: string;
}

interface TrackingResult {
  orderNumber: string;
  status: TrackingStatus;
  customerName: string;
  estimatedDelivery: string;
  placedDate: string;
  items: { name: string; qty: number }[];
  updates: { date: string; time: string; message: string; location: string }[];
}

const TRACKING_STEPS: TrackingStep[] = [
  { id: 'confirmed', label: 'Order Confirmed', description: 'Your order has been received and confirmed', icon: 'ri-file-check-line' },
  { id: 'processing', label: 'Processing', description: 'Your order is being prepared and packed', icon: 'ri-settings-3-line' },
  { id: 'dispatched', label: 'Dispatched', description: 'Your order has left our warehouse', icon: 'ri-store-line' },
  { id: 'in_transit', label: 'In Transit', description: 'Your order is on its way to you', icon: 'ri-truck-line' },
  { id: 'delivered', label: 'Delivered', description: 'Your order has been delivered', icon: 'ri-home-smile-line' },
];

const STATUS_ORDER: TrackingStatus[] = ['confirmed', 'processing', 'dispatched', 'in_transit', 'delivered'];

// Mock tracking data — in a real app this would come from an API
const MOCK_ORDERS: Record<string, TrackingResult> = {
  'PEL-2026-0042': {
    orderNumber: 'PEL-2026-0042',
    status: 'in_transit',
    customerName: 'Thomas Dubois',
    estimatedDelivery: 'April 22, 2026',
    placedDate: 'April 4, 2026',
    items: [
      { name: 'Vertical Column Radiator 1800mm', qty: 2 },
      { name: 'Horizontal Panel Radiator 600mm', qty: 1 },
    ],
    updates: [
      { date: 'Apr 10', time: '09:14', message: 'Package in transit — arrived at sorting hub', location: 'Brussels, Belgium' },
      { date: 'Apr 8', time: '14:32', message: 'Shipment dispatched from warehouse', location: 'Tienen, Belgium' },
      { date: 'Apr 7', time: '11:05', message: 'Order packed and ready for dispatch', location: 'Tienen, Belgium' },
      { date: 'Apr 4', time: '16:20', message: 'Order confirmed and payment received', location: 'Online' },
    ],
  },
  'PEL-2026-0031': {
    orderNumber: 'PEL-2026-0031',
    status: 'delivered',
    customerName: 'Sophie Martens',
    estimatedDelivery: 'March 28, 2026',
    placedDate: 'March 12, 2026',
    items: [
      { name: 'Designer Towel Rail Radiator', qty: 1 },
    ],
    updates: [
      { date: 'Mar 28', time: '13:45', message: 'Package delivered successfully', location: 'Ghent, Belgium' },
      { date: 'Mar 27', time: '08:10', message: 'Out for delivery', location: 'Ghent, Belgium' },
      { date: 'Mar 25', time: '17:30', message: 'Arrived at local delivery depot', location: 'Ghent, Belgium' },
      { date: 'Mar 20', time: '10:00', message: 'Shipment dispatched from warehouse', location: 'Tienen, Belgium' },
      { date: 'Mar 12', time: '14:55', message: 'Order confirmed and payment received', location: 'Online' },
    ],
  },
  'PEL-2026-0058': {
    orderNumber: 'PEL-2026-0058',
    status: 'processing',
    customerName: 'Jan Van Der Berg',
    estimatedDelivery: 'April 28, 2026',
    placedDate: 'April 10, 2026',
    items: [
      { name: 'Anthracite Flat Panel Radiator 900mm', qty: 3 },
      { name: 'Thermostatic Valve Set', qty: 3 },
    ],
    updates: [
      { date: 'Apr 11', time: '09:00', message: 'Order is being prepared and packed', location: 'Tienen, Belgium' },
      { date: 'Apr 10', time: '18:42', message: 'Order confirmed and payment received', location: 'Online' },
    ],
  },
};

function getStepIndex(status: TrackingStatus): number {
  return STATUS_ORDER.indexOf(status);
}

export default function OrderTrackingPage() {
  const { getCartCount } = useCart();
  const [orderInput, setOrderInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [result, setResult] = useState<TrackingResult | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    document.title = 'Track Your Order | PEL Partner Company';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Track your PEL Partner Company radiator order in real time. Enter your order number to see delivery status, estimated arrival, and shipping updates.');

    const schemaId = 'schema-track-order';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Track Your Order — PEL Partner Company',
        description: 'Track your PEL Partner Company radiator order in real time. Enter your order number to see delivery status, estimated arrival, and shipping updates.',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/track-order`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL || 'https://example.com' },
            { '@type': 'ListItem', position: 2, name: 'Track Order', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/track-order` },
          ],
        },
      });
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const handleTrack = (e: React.FormEvent) => {
    e.preventDefault();
    setSearched(true);
    const found = MOCK_ORDERS[orderInput.trim().toUpperCase()];
    if (found) {
      setResult(found);
      setNotFound(false);
    } else {
      setResult(null);
      setNotFound(true);
    }
  };

  const handleReset = () => {
    setResult(null);
    setNotFound(false);
    setSearched(false);
    setOrderInput('');
    setEmailInput('');
  };

  const currentStepIndex = result ? getStepIndex(result.status) : -1;

  return (
    <div className="min-h-screen bg-[#f9f9f7]">
      <Navbar cartItemCount={getCartCount()} />

      <div className="pt-32 pb-20">
        <div className="max-w-[860px] mx-auto px-6">

          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-5">
              <i className="ri-map-pin-time-line text-white text-3xl"></i>
            </div>
            <h1 className="font-serif text-4xl font-bold text-black mb-3">Track Your Order</h1>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              Enter your order number to get real-time delivery updates. Orders typically arrive within <strong>12 working days</strong>.
            </p>
          </div>

          {/* Search Form */}
          {!result && (
            <div className="bg-white rounded-2xl p-8 mb-8">
              <form onSubmit={handleTrack} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Order Number
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                      <i className="ri-receipt-line text-gray-400"></i>
                    </div>
                    <input
                      type="text"
                      value={orderInput}
                      onChange={(e) => setOrderInput(e.target.value)}
                      placeholder="e.g. PEL-2026-0042"
                      required
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa] font-mono tracking-wide"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Your order number was included in your confirmation email.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line text-gray-400"></i>
                    </div>
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa]"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full bg-black text-white py-4 rounded-full text-sm font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 mt-2"
                >
                  <i className="ri-search-line"></i>
                  Track Order
                </button>
              </form>

              {/* Demo hint */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-xs text-gray-400 mb-3 font-medium uppercase tracking-wide">Try a demo order number:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(MOCK_ORDERS).map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setOrderInput(num)}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-mono text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Not Found */}
          {notFound && !result && (
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-eye-line text-red-400 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Order Not Found</h3>
              <p className="text-sm text-gray-500 mb-6">
                We couldn't find an order with number <strong className="font-mono">{orderInput}</strong>. Please double-check your confirmation email.
              </p>
              <button
                onClick={handleReset}
                className="px-6 py-3 bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Tracking Result */}
          {result && (
            <div className="space-y-6">

              {/* Order Header Card */}
              <div className="bg-white rounded-2xl p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Order Number</p>
                    <h2 className="text-xl font-bold font-mono text-black">{result.orderNumber}</h2>
                    <p className="text-sm text-gray-500 mt-1">Placed on {result.placedDate} &bull; {result.customerName}</p>
                  </div>
                  <div className="flex flex-col items-start sm:items-end gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                      result.status === 'delivered'
                        ? 'bg-green-100 text-green-700'
                        : result.status === 'in_transit'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        result.status === 'delivered' ? 'bg-green-500' : result.status === 'in_transit' ? 'bg-amber-500' : 'bg-gray-500'
                      }`}></span>
                      {TRACKING_STEPS.find(s => s.id === result.status)?.label}
                    </span>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      Est. delivery: <strong className="text-gray-700 ml-1">{result.estimatedDelivery}</strong>
                    </p>
                  </div>
                </div>

                {/* Items */}
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">Items in this order</p>
                  <div className="space-y-2">
                    {result.items.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-box-3-line text-gray-400"></i>
                        </div>
                        <span>{item.name}</span>
                        <span className="text-gray-400">&times; {item.qty}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Progress Tracker */}
              <div className="bg-white rounded-2xl p-6">
                <h3 className="text-base font-bold text-black mb-8">Delivery Progress</h3>
                <div className="relative">
                  {/* Progress line */}
                  <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-gray-100"></div>
                  <div
                    className="absolute left-5 top-5 w-0.5 bg-black transition-all duration-700"
                    style={{ height: `${(currentStepIndex / (TRACKING_STEPS.length - 1)) * 100}%` }}
                  ></div>

                  <div className="space-y-0">
                    {TRACKING_STEPS.map((step, index) => {
                      const isCompleted = index <= currentStepIndex;
                      const isCurrent = index === currentStepIndex;
                      return (
                        <div key={step.id} className="relative flex items-start gap-5 pb-8 last:pb-0">
                          {/* Circle */}
                          <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                            isCompleted
                              ? 'bg-black text-white'
                              : 'bg-gray-100 text-gray-400'
                          } ${isCurrent ? 'ring-4 ring-black/10' : ''}`}>
                            <i className={`${step.icon} text-base`}></i>
                          </div>
                          {/* Content */}
                          <div className="pt-1.5 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className={`text-sm font-semibold ${isCompleted ? 'text-black' : 'text-gray-400'}`}>
                                {step.label}
                              </p>
                              {isCurrent && (
                                <span className="text-xs bg-black text-white px-2 py-0.5 rounded-full">Current</span>
                              )}
                            </div>
                            <p className={`text-xs mt-0.5 ${isCompleted ? 'text-gray-500' : 'text-gray-300'}`}>
                              {step.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Activity Log */}
              <div className="bg-white rounded-2xl p-6">
                <h3 className="text-base font-bold text-black mb-6">Tracking Updates</h3>
                <div className="space-y-4">
                  {result.updates.map((update, i) => (
                    <div key={i} className={`flex gap-4 ${i === 0 ? '' : 'opacity-70'}`}>
                      <div className="flex flex-col items-center gap-1 flex-shrink-0 w-14">
                        <span className="text-xs font-semibold text-gray-700">{update.date}</span>
                        <span className="text-xs text-gray-400">{update.time}</span>
                      </div>
                      <div className="flex items-start gap-3 flex-1 pb-4 border-b border-gray-50 last:border-0 last:pb-0">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${i === 0 ? 'bg-black' : 'bg-gray-300'}`}></div>
                        <div>
                          <p className="text-sm text-gray-800 font-medium">{update.message}</p>
                          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                            <i className="ri-map-pin-line"></i>
                            {update.location}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Delivery Info */}
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 text-white">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-customer-service-2-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Need help with your order?</p>
                      <p className="text-xs text-gray-400 mt-0.5">Our team is available Mon–Fri, 9:00–18:00 CET</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <a
                      href="mailto:pelpartnercompany@gmail.com"
                      className="px-4 py-2.5 bg-white text-black rounded-full text-xs font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                    >
                      <i className="ri-mail-line"></i>
                      Email Us
                    </a>
                    <a
                      href="tel:+32487261970"
                      className="px-4 py-2.5 bg-white/10 text-white rounded-full text-xs font-semibold hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                    >
                      <i className="ri-phone-line"></i>
                      Call Us
                    </a>
                  </div>
                </div>
              </div>

              {/* Track another */}
              <div className="text-center">
                <button
                  onClick={handleReset}
                  className="text-sm text-gray-500 hover:text-black transition-colors cursor-pointer flex items-center gap-1.5 mx-auto"
                >
                  <i className="ri-arrow-left-line"></i>
                  Track a different order
                </button>
              </div>
            </div>
          )}

          {/* Info Cards */}
          {!result && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              {[
                { icon: 'ri-time-line', title: '~12 Working Days', desc: 'Standard delivery time from order confirmation' },
                { icon: 'ri-truck-line', title: 'Free Delivery', desc: 'Belgium €3,000+ · Outside Belgium €5,000+' },
                { icon: 'ri-mail-send-line', title: 'Email Updates', desc: 'Tracking updates sent to your email automatically' },
              ].map((card) => (
                <div key={card.title} className="bg-white rounded-2xl p-5 text-center">
                  <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <i className={`${card.icon} text-gray-600 text-lg`}></i>
                  </div>
                  <p className="text-sm font-semibold text-black mb-1">{card.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>

      <Footer />
    </div>
  );
}
